import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { MacroToolsStrip } from "@/components/site/MacroToolsStrip";
import { GoldKillzonesClock } from "@/components/tools/GoldKillzonesClock";
import { KILLZONES, NY_SUMMER_OFFSET, NY_WINTER_OFFSET, SILVER_BULLETS, fmtClock } from "@/lib/killzones";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-killzones-clock";
const TITLE = "Gold Killzones Clock: Live ICT Killzone Times | XAU Pro Academy";
const DESC =
  "Live ICT killzones for gold (XAUUSD) in your time zone. London and New York killzones, Silver Bullet hours, the Asian range, LBMA gold fix times and today's news freeze, adjusted for daylight saving.";

const FAQ = [
  {
    q: "What are killzones in trading?",
    a: "Killzones are recurring time windows when institutional order flow is concentrated, so price is more likely to raid liquidity and deliver a directional move. The term comes from ICT teaching. The main windows are the London killzone and the New York AM killzone.",
  },
  {
    q: "What time is the London killzone?",
    a: "02:00 to 05:00 New York time. That is 07:00 to 10:00 UTC in northern winter and 06:00 to 09:00 UTC in summer. The clock above converts it to your local time automatically.",
  },
  {
    q: "What time is the New York killzone?",
    a: "The New York AM killzone runs from 07:00 to 10:00 New York time, which is 12:00 to 15:00 UTC in winter and 11:00 to 14:00 UTC in summer. US data such as CPI and NFP is released at 08:30 New York time inside this window.",
  },
  {
    q: "What is the ICT Silver Bullet?",
    a: "Three one-hour windows in New York time: 03:00 to 04:00, 10:00 to 11:00 and 14:00 to 15:00. Traders look for a liquidity sweep followed by displacement and a fair value gap inside the hour. The window narrows when to look, it does not create a setup on its own.",
  },
  {
    q: "Why are killzones measured in New York time?",
    a: "ICT defines every window on the New York clock. Because the US and UK change clocks on different dates, the London killzone in UTC or London time shifts by an hour for a few weeks each spring and autumn. The clock follows the New York definition, so it stays correct through those weeks.",
  },
  {
    q: "Which killzone is best for gold?",
    a: "The New York AM killzone usually carries the largest gold ranges because London and New York are both open and US data lands inside it. The London killzone is the second strongest window and often sets the high or low of the day.",
  },
  {
    q: "What is the Asian range in gold?",
    a: "The range gold builds from 20:00 to 00:00 New York time while participation is thin. Its high and low hold resting stops, which makes them the first liquidity target for London.",
  },
  {
    q: "What is a Judas Swing?",
    a: "A false move near the London open that runs one side of the Asian range, takes the stops resting there, then reverses. It is only identifiable after displacement in the opposite direction confirms it. Never assume the first move must reverse.",
  },
  {
    q: "What time is the London gold fix?",
    a: "The LBMA Gold Price auction runs twice a day, at 10:30 and 15:00 London time. Physical and OTC flow concentrates around both auctions, so short-term volatility can rise near them.",
  },
  {
    q: "Should I trade every killzone?",
    a: "No. A killzone tells you when a setup is more likely to form, not whether one exists. Without a liquidity sweep, displacement and a body close beyond structure, the correct decision inside a killzone is to stand aside.",
  },
];

const RELATED = [
  { to: "/gold-trading-sessions", label: "Sessions", title: "Gold trading hours clock", body: "Sydney, Tokyo, London and New York sessions in your time zone." },
  { to: "/xauusd-volatility-heatmap", label: "Timing edge", title: "XAUUSD volatility heatmap", body: "Measured gold range for every weekday and hour from 90 days of data." },
  { to: "/position-size-calculator", label: "Size the trade", title: "Gold lot size calculator", body: "Turn your stop distance into the correct XAUUSD lot size before the killzone opens." },
  { to: "/gold-daily-range-atr", label: "Range", title: "Gold daily range and ATR", body: "How much of today's typical range gold has already used." },
] as const;

export const Route = createFileRoute("/gold-killzones-clock")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "gold killzones, ict killzones, killzone times, london killzone time, new york killzone time, ict silver bullet time, silver bullet hours, killzones est, killzones utc, asian range gold, judas swing gold, xauusd killzones, london gold fix time, lbma gold price time, best time to trade gold ict",
      },
      { property: "og:title", content: "Gold Killzones Clock: Live ICT Killzones for XAUUSD" },
      {
        property: "og:description",
        content: "London and New York killzones, Silver Bullet hours and gold fix times in your local time, adjusted for daylight saving.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Gold Killzones Clock: Live ICT Killzones for XAUUSD" },
      { name: "twitter:description", content: "London and New York killzones, Silver Bullet hours and gold fix times in your local time." },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Gold Killzones Clock",
          url: `${SITE}${PATH}`,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Any",
          description: DESC,
          featureList: [
            "Live ICT killzone status for XAUUSD",
            "London and New York killzones in local time",
            "ICT Silver Bullet hours",
            "LBMA gold price auction times",
            "High impact news freeze windows",
          ],
          browserRequirements: "Requires JavaScript. Modern HTML5 browser",
          isAccessibleForFree: true,
          inLanguage: "en",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/OnlineOnly" },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Gold Trading Hours", item: `${SITE}/gold-trading-sessions` },
            { "@type": "ListItem", position: 3, name: "Gold Killzones Clock", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  component: GoldKillzonesPage,
});

const ny = (m: number) => fmtClock(m);
const utc = (m: number, off: number) => fmtClock(m - off);

function A({ slug, children }: { slug: string; children: React.ReactNode }) {
  return (
    <Link to="/education/$slug" params={{ slug }} className="text-gold hover:underline">
      {children}
    </Link>
  );
}

function GoldKillzonesPage() {
  return (
    <Layout>
      <section className="container mx-auto max-w-7xl px-4 pt-14 md:pt-20">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Free timing tool</div>
        <h1 className="mt-3 max-w-3xl font-display text-3xl font-semibold leading-tight md:text-5xl">
          Gold Killzones Clock: Live ICT Killzones for XAUUSD
        </h1>
        <p className="mt-4 max-w-2xl text-muted-foreground md:text-lg">
          Which killzone gold is in right now, when the next one opens in your local time, and where today's Silver Bullet hours,
          gold fix auctions and news releases fall.
        </p>
      </section>

      <section className="container mx-auto mt-10 max-w-5xl px-4">
        <GoldKillzonesClock />
        <p className="mt-3 text-[11px] text-muted-foreground">
          All windows follow the New York clock and adjust for daylight saving automatically. Educational timing context, not a trade signal.
        </p>
      </section>

      <section className="container mx-auto mt-16 max-w-4xl px-4">
        <h2 className="font-display text-2xl font-semibold md:text-3xl">ICT killzone times for gold</h2>
        <p className="mt-4 text-muted-foreground">
          Reference table in New York time with the UTC equivalent for each half of the year. Winter runs from early November to mid March.
        </p>
        <div className="mt-5 overflow-x-auto rounded-xl border border-border/60">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-muted/30 text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Window</th>
                <th className="p-3">New York</th>
                <th className="p-3">UTC (winter)</th>
                <th className="p-3">UTC (summer)</th>
                <th className="p-3">Liquidity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {KILLZONES.map((k) => (
                <tr key={k.key}>
                  <td className="p-3 font-medium">{k.name}</td>
                  <td className="p-3 font-mono text-muted-foreground">{ny(k.start)} to {ny(k.end)}</td>
                  <td className="p-3 font-mono text-muted-foreground">{utc(k.start, NY_WINTER_OFFSET)} to {utc(k.end, NY_WINTER_OFFSET)}</td>
                  <td className="p-3 font-mono text-muted-foreground">{utc(k.start, NY_SUMMER_OFFSET)} to {utc(k.end, NY_SUMMER_OFFSET)}</td>
                  <td className="p-3 text-muted-foreground">{k.liquidity}</td>
                </tr>
              ))}
              {SILVER_BULLETS.map((s) => (
                <tr key={s.key}>
                  <td className="p-3 font-medium">{s.name}</td>
                  <td className="p-3 font-mono text-muted-foreground">{ny(s.start)} to {ny(s.end)}</td>
                  <td className="p-3 font-mono text-muted-foreground">{utc(s.start, NY_WINTER_OFFSET)} to {utc(s.end, NY_WINTER_OFFSET)}</td>
                  <td className="p-3 font-mono text-muted-foreground">{utc(s.start, NY_SUMMER_OFFSET)} to {utc(s.end, NY_SUMMER_OFFSET)}</td>
                  <td className="p-3 text-muted-foreground">One hour</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">What a killzone is, and what it is not</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            A killzone is a window when large participants are most active, so price is more likely to reach for{" "}
            <A slug="liquidity-sweep-gold-trading">resting liquidity</A> and then deliver a directional leg. Outside these windows gold
            tends to drift, range and reverse without follow through.
          </p>
          <p>
            Time is a filter, not a trigger. A killzone raises the probability that a setup forms, but the setup still needs a liquidity
            sweep, <A slug="ict-displacement-explained">displacement</A> and a candle body close beyond structure. A wick through a level
            inside a killzone is still only a sweep.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">The Asian range: where London finds its liquidity</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            From 20:00 to midnight New York time gold usually compresses. Retail stops accumulate above the range high and below the range
            low. Mark both levels before London opens. They are the first external liquidity of the new day.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">London killzone and the Judas Swing</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            Between 02:00 and 05:00 New York time London takes control. A common sequence is a run through one side of the Asian range, a
            stop raid, then a reversal with displacement. That false first move is called the Judas Swing.
          </p>
          <p>
            The pattern is only visible once the reversal leaves a <A slug="fair-value-gap-xauusd-guide">fair value gap</A> and closes
            beyond structure. Read the daily sequence in the <A slug="amd-pattern-gold-daily-cycles">AMD pattern in gold daily cycles</A>.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">New York AM killzone: the main delivery window</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            From 07:00 to 10:00 New York time both centres are open, futures volume peaks and US data lands at 08:30. Gold often either
            continues the London leg or reverses it here. Plan entries from{" "}
            <A slug="premium-discount-zones-gold">premium and discount zones</A> rather than chasing the release candle.
          </p>
          <p>
            CPI, NFP and FOMC can move gold sharply in seconds. The clock shades each high impact release from 15 minutes before to 30
            minutes after. See the history in <Link to="/gold-inflation-cpi" className="text-gold hover:underline">US CPI and gold</Link>{" "}
            and <Link to="/gold-nfp-employment" className="text-gold hover:underline">NFP and gold</Link>.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">ICT Silver Bullet hours</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            The Silver Bullet narrows the search to three single hours: 03:00, 10:00 and 14:00 New York time. Inside each hour the model
            looks for a sweep of a nearby high or low, displacement away from it and a fair value gap to rebalance into.
          </p>
          <p>
            On gold, the AM Silver Bullet overlaps the London close and the LBMA afternoon auction, so moves can be fast. A one-hour window
            does not justify a tight stop. Size from structure with the{" "}
            <Link to="/position-size-calculator" className="text-gold hover:underline">position size calculator</Link>.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">Gold-specific reference times</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            <span className="text-foreground">LBMA Gold Price:</span> the London benchmark auctions run at 10:30 and 15:00 London time.
            Physical, central bank and OTC flow concentrates around them.
          </p>
          <p>
            <span className="text-foreground">COMEX settlement:</span> gold futures settle at 13:30 New York time. Participation thins after
            it, which is why the New York PM window usually fades.
          </p>
          <p>
            <span className="text-foreground">Daily rollover:</span> at 17:00 New York time the trading day rolls and spreads widen for
            about an hour. Avoid new entries and tight stops around it.
          </p>
        </div>

        <h2 className="mt-12 font-display text-2xl font-semibold md:text-3xl">Daylight saving and killzones</h2>
        <div className="mt-4 space-y-4 text-muted-foreground">
          <p>
            The US and UK change clocks on different weekends in March and in late October or early November. For those two to three
            weeks the London killzone sits an hour differently against London time. Because the clock reads the New York definition
            directly from the time zone database, it stays correct without manual adjustment.
          </p>
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-7xl px-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Related tools</div>
        <h2 className="mt-3 font-display text-2xl font-semibold md:text-3xl">Plan the trade around the window</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {RELATED.map((n) => (
            <Link key={n.to} to={n.to} className="group rounded-xl border border-border/60 bg-card/60 p-5 transition-colors hover:border-gold/50">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{n.label}</div>
              <div className="mt-2 font-medium text-foreground group-hover:text-gold">{n.title}</div>
              <p className="mt-2 text-sm text-muted-foreground">{n.body}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-7xl px-4">
        <div className="rounded-2xl border border-gold/30 bg-card/70 p-6 md:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Timing is one filter</div>
          <h2 className="mt-3 max-w-2xl font-display text-2xl font-semibold md:text-3xl">The clock tells you when. Structure tells you whether.</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Every published setup on the desk is qualified inside liquid hours and paused around high impact releases. The verified
            record shows how that discipline performs over time.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild variant="gold">
              <Link to="/track-record">
                See the track record <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/xauusd-analysis-today">XAUUSD analysis today</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto mt-16 max-w-4xl px-4 pb-6">
        <h2 className="font-display text-2xl font-semibold md:text-3xl">Frequently asked questions</h2>
        <div className="mt-6 divide-y divide-border/60 rounded-xl border border-border/60 bg-card/60">
          {FAQ.map((f) => (
            <details key={f.q} className="group p-5">
              <summary className="cursor-pointer list-none font-medium text-foreground">{f.q}</summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <MacroToolsStrip />
      <div className="pb-16" />
    </Layout>
  );
}
